import React from 'react';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import Message from 'shared/components/UC/Message';
import Modal from 'shared/components/UC/Modal';
import styles from './SendInvitationsModal.module.css';

export interface InvitationSummary {
  /** Evaluadores seleccionados con al menos una asignación sin invitar. */
  evaluators: number;
  /** Asignaciones que quedarán marcadas como invitadas. */
  assignments: number;
  /** Cuántos de esos evaluadores aún no tienen contraseña. */
  newEvaluators: number;
}

export interface InvitationResult {
  emails: number;
  assignments: number;
  newEvaluators: number;
}

interface SendInvitationsModalProps {
  isOpen: boolean;
  sending: boolean;
  summary: InvitationSummary;
  result: InvitationResult | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const SendInvitationsModal: React.FC<SendInvitationsModalProps> = ({
  isOpen,
  sending,
  summary,
  result,
  onCancel,
  onConfirm,
}) => {
  if (result) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onCancel}
        title="Invitaciones enviadas"
        size="md"
        footer={
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cerrar
          </Button>
        }
      >
        <Message variant="success" title="Envío realizado">
          Se enviaron <strong>{result.emails} {plural(result.emails, 'correo', 'correos')}</strong> que
          cubren <strong>{result.assignments} {plural(result.assignments, 'asignación', 'asignaciones')}</strong>.
        </Message>
        <p className={styles.note}>
          Las postulaciones involucradas pasan de <strong>Sin invitar</strong> a{' '}
          <strong>Con invitación</strong>. A partir de ahora empieza a correr el
          plazo de respuesta.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="Enviar invitaciones"
      size="md"
      footer={
        <>
          <Button type="button" variant="primary" onClick={onCancel} disabled={sending}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onConfirm}
            loading={sending}
            loadingLabel="Enviando..."
            disabled={summary.evaluators === 0}
          >
            Enviar invitaciones
          </Button>
        </>
      }
    >
      <div className={styles.figures}>
        <div className={styles.figure}>
          <span className={`uc-kpi-xl ${styles.value}`}>{summary.evaluators}</span>
          <span className={`uc-p-body-small ${styles.label}`}>
            {plural(summary.evaluators, 'correo a enviar', 'correos a enviar')}
          </span>
        </div>
        <div className={styles.figure}>
          <span className={`uc-kpi-xl ${styles.value}`}>{summary.assignments}</span>
          <span className={`uc-p-body-small ${styles.label}`}>
            {plural(summary.assignments, 'asignación cubierta', 'asignaciones cubiertas')}
          </span>
        </div>
      </div>

      <p className={styles.lead}>
        Cada evaluador recibe <strong>un solo correo</strong> con todas las
        postulaciones que se le asignaron, no uno por postulación.
      </p>

      {summary.newEvaluators > 0 && (
        <p className={styles.note}>
          <strong>{summary.newEvaluators}</strong>{' '}
          {plural(summary.newEvaluators, 'es un evaluador nuevo', 'son evaluadores nuevos')}: su
          correo lleva el enlace para crear la contraseña antes de entrar al panel.
        </p>
      )}

      <p className={styles.note}>
        <a href="/email-mockups.html#e7" target="_blank" rel="noopener noreferrer">
          Ver la maqueta del correo
        </a>
      </p>

      <Alert variant="warning">
        El envío no se puede deshacer. Los evaluadores que ya fueron invitados no
        reciben un correo nuevo.
      </Alert>
    </Modal>
  );
};

export default SendInvitationsModal;
